import dayjs, { Dayjs } from 'dayjs';

import { SlaStatus } from '../../../../api/enumerations';

export interface DeadlineTrackingProps {
  slaStatus: SlaStatus | null;
  slaDaysElapsed: number | null;
  slaDaysRemaining: number | null;
  requestCreatedAt: string;
  requestLimitDate: string;
}

interface DeadlineProgressReturn {
  elapsedDays: number;
  totalDays: number;
  progressPercent: number;
}

export function useDeadlineProgress(
  props: DeadlineTrackingProps
): DeadlineProgressReturn {
  const {
    slaStatus,
    slaDaysElapsed,
    slaDaysRemaining,
    requestCreatedAt,
    requestLimitDate,
  } = props;

  const shouldUseDynamicDates =
    slaDaysElapsed === null && slaStatus === SlaStatus.ON_TRACK;

  const normalizeToDate = (dateStr: string): Dayjs => {
    return dayjs(dateStr.split('T')[0]).startOf('day');
  };

  const calculateElapsedDays = (): number => {
    if (shouldUseDynamicDates) {
      const now = dayjs().startOf('day');
      const requestCreated = normalizeToDate(requestCreatedAt);
      return now.diff(requestCreated, 'day');
    }
    return slaDaysElapsed ?? 0;
  };

  const calculateTotalDays = (): number => {
    if (shouldUseDynamicDates) {
      const limit = normalizeToDate(requestLimitDate);
      const requestCreated = normalizeToDate(requestCreatedAt);
      return limit.diff(requestCreated, 'day');
    }
    return (slaDaysElapsed ?? 0) + Math.max(slaDaysRemaining ?? 0, 0);
  };

  const isOverdue =
    (slaDaysRemaining ?? 0) < 0 || slaStatus === SlaStatus.OVERDUE;

  const calculateProgressPercent = (
    elapsed: number,
    total: number,
    overdue: boolean
  ): number => {
    if (overdue) return 100;
    if (total <= 0) return 0;
    if (slaDaysRemaining === 0) return 99;
    return Math.min((elapsed / total) * 100, 100);
  };

  const elapsedDays = calculateElapsedDays();
  const totalDays = calculateTotalDays();
  const progressPercent = calculateProgressPercent(
    elapsedDays,
    totalDays,
    isOverdue
  );

  return {
    elapsedDays,
    totalDays,
    progressPercent,
  };
}
